import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { SnackbarSeverityType } from '../../../enums';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import {
  StudentTransactionMatrixItem,
  ModeOfPayment,
  TransactionStatus,
  StudentPaymentSchemeWithMetrics
} from '../../../types/paymentScheme';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { CHEQUE_BOUNCE_REASONS } from '../constants/chequeBounceReasons';
import { bankOptions } from '../../../constants/banks';

interface UpdatePaymentStatusDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: StudentTransactionMatrixItem | null;
  onUpdate: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
  paymentSchemeMatrix: StudentPaymentSchemeWithMetrics | null;
}

const paymentModeOptions: DropdownSelectOption[] = Object.values(
  ModeOfPayment
).map((mode) => ({
  label: mode,
  value: mode as string
}));

const paymentStatusOptions: DropdownSelectOption[] = Object.values(
  TransactionStatus
).map((status) => ({
  label: status,
  value: status as string
}));

const chequeBounceReasonOptions: DropdownSelectOption[] =
  CHEQUE_BOUNCE_REASONS.map((reason) => ({
    label: reason,
    value: reason
  }));

const UpdatePaymentStatusDialog: React.FC<UpdatePaymentStatusDialogProps> = ({
  open,
  onClose,
  transaction,
  onUpdate,
  onShowToast,
  paymentSchemeMatrix
}) => {
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentDate, setPaymentDate] = useState<Dayjs | null>(null);
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [depositUptoDate, setDepositUptoDate] = useState<Dayjs | null>(null);
  const [remarks, setRemarks] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [originalPaymentStatus, setOriginalPaymentStatus] = useState('');
  const [chequeBounceReason, setChequeBounceReason] = useState('');
  const [paymentFailureReason, setPaymentFailureReason] = useState('');
  const [initialStatusChangeDate, setInitialStatusChangeDate] =
    useState<Dayjs | null>(null);
  const [userSelectedStatusChangeDate, setUserSelectedStatusChangeDate] =
    useState<Dayjs | null>(null);
  const [transactionId, setTransactionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetState = useCallback(() => {
    setAmountPaid('');
    setPaymentMode('');
    setPaymentDate(null);
    setChequeNumber('');
    setBankName('');
    setBranchName('');
    setDepositUptoDate(null);
    setRemarks('');
    setPaymentStatus('');
    setOriginalPaymentStatus('');
    setChequeBounceReason('');
    setPaymentFailureReason('');
    setInitialStatusChangeDate(null);
    setUserSelectedStatusChangeDate(null);
    setTransactionId('');
    setIsLoading(false);
  }, []);

  const getLatestStatusChangeDateFromTransaction = useCallback(
    (currentTransaction: StudentTransactionMatrixItem | null) => {
      if (
        currentTransaction?.transactionStatusLogs &&
        currentTransaction.transactionStatusLogs.length > 0
      ) {
        const sortedLogs = currentTransaction.transactionStatusLogs.sort(
          (a, b) =>
            new Date(b.statusChangeDate).getTime() -
            new Date(a.statusChangeDate).getTime()
        );
        return dayjs(sortedLogs[0].statusChangeDate);
      }
      return null;
    },
    []
  );

  useEffect(() => {
    if (transaction) {
      setAmountPaid(transaction.amount?.toString() || '');
      setPaymentMode(transaction.modeOfPayment || '');
      setPaymentDate(
        transaction.paymentDate ? dayjs(transaction.paymentDate) : null
      );
      setChequeNumber(transaction.chequeDetails?.[0]?.chequeNo || '');
      setBankName(transaction.chequeDetails?.[0]?.bank || '');
      setBranchName(transaction.chequeDetails?.[0]?.branch || '');
      setDepositUptoDate(
        transaction.feeDepositedUpto
          ? dayjs(transaction.feeDepositedUpto)
          : null
      );
      setRemarks(transaction.remark || '');
      setPaymentStatus(transaction.status || '');
      setOriginalPaymentStatus(transaction.status || '');
      setChequeBounceReason(
        transaction.modeOfPayment === ModeOfPayment.CHEQUE
          ? transaction?.failureReason || ''
          : ''
      );
      setPaymentFailureReason(
        transaction.modeOfPayment !== ModeOfPayment.CHEQUE
          ? transaction?.failureReason || ''
          : ''
      );
      setTransactionId(transaction.transactionId || '');

      const latestLogDate =
        getLatestStatusChangeDateFromTransaction(transaction);
      setInitialStatusChangeDate(latestLogDate);
      setUserSelectedStatusChangeDate(latestLogDate);
    }
  }, [transaction, getLatestStatusChangeDateFromTransaction, open]);

  const isChequeOrDemandDraft =
    paymentMode === ModeOfPayment.CHEQUE ||
    paymentMode === ModeOfPayment.DEMAND_DRAFT;
  const isPaymentStatusChanged = paymentStatus !== originalPaymentStatus;
  const isBouncedCheque =
    paymentMode === ModeOfPayment.CHEQUE &&
    paymentStatus === TransactionStatus.BOUNCED;
  const showPaymentFailureReason = paymentMode !== ModeOfPayment.CHEQUE;

  const isSaveDisabled =
    !amountPaid ||
    parseFloat(amountPaid) <= 0 ||
    !paymentMode ||
    !paymentDate ||
    !depositUptoDate ||
    !paymentStatus ||
    (isChequeOrDemandDraft && (!chequeNumber || !bankName || !branchName)) ||
    (isBouncedCheque && !chequeBounceReason) ||
    isLoading;

  const handleSave = async () => {
    if (!transaction?.receiptNo) {
      onShowToast('Receipt No is missing.', SnackbarSeverityType.error);
      return;
    }

    setIsLoading(true);
    try {
      let failureReasonPayload: string | undefined;
      if (isBouncedCheque) {
        failureReasonPayload = chequeBounceReason;
      } else if (showPaymentFailureReason) {
        failureReasonPayload = paymentFailureReason;
      } else {
        failureReasonPayload = undefined;
      }

      const paymentTransactionPayload = {
        paymentDate: paymentDate?.format('YYYY-MM-DD') || '',
        status: paymentStatus,
        remark: remarks,
        failureReason: failureReasonPayload,
        feeDepositedUpto: depositUptoDate?.format('YYYY-MM-DD') || '',
        statusChangeDate: isPaymentStatusChanged
          ? userSelectedStatusChangeDate?.format('YYYY-MM-DD') || ''
          : initialStatusChangeDate?.format('YYYY-MM-DD') || '',
        transactionId: transactionId
      };

      await paymentSchemesApis.updatePaymentTransactionStatus(
        transaction.receiptNo,
        paymentTransactionPayload
      );

      if (isChequeOrDemandDraft) {
        const chequePayload = {
          id: transaction.chequeDetails?.[0]?.id || '',
          chequeNo: chequeNumber,
          bank: bankName,
          branch: branchName,
          issueDate: paymentDate?.format('YYYY-MM-DD') || ''
        };
        await paymentSchemesApis.updateChequeStatus(chequePayload);
      }

      onShowToast(
        'Payment status updated successfully!',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to update payment status.',
        SnackbarSeverityType.error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleSave}
      sx={{ textTransform: 'none' }}
      disabled={isSaveDisabled || isLoading}
    >
      Save
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Update Fee Payment Status'
      maxWidth='sm'
      actions={dialogActions}
    >
      {isLoading && <OverlayLoader show={isLoading} />}
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Particulars'
          placeholder='Enter Particulars'
          value={paymentSchemeMatrix?.particulars || ''}
          fullWidth
          hasTopLabel
          disabled
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Amount Payable'
          placeholder='Enter Amount Payable'
          value={paymentSchemeMatrix?.amount?.toString() || ''}
          fullWidth
          hasTopLabel
          disabled
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Amount paid'
          placeholder='Enter Amount Paid'
          type='number'
          value={amountPaid}
          onChange={(e) => setAmountPaid(e.target.value)}
          fullWidth
          hasTopLabel
          disabled
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <DropdownSelect
          label='Payment mode'
          placeholder='Select Payment Mode'
          value={paymentMode}
          options={paymentModeOptions}
          onChange={(e) => setPaymentMode(e.target.value as string)}
          name='paymentMode'
          fullWidth
          hasTopLabel
          required
          disabled
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomDatePicker
            label={isChequeOrDemandDraft ? 'Date of Issue' : 'Payment Date'}
            onChange={(newValue: Dayjs | null) => setPaymentDate(newValue)}
            value={paymentDate}
            required
          />
        </LocalizationProvider>
      </Box>

      {isChequeOrDemandDraft && (
        <>
          <Box sx={{ mb: 2 }}>
            <TopLabelTextField
              label='Cheque number'
              placeholder='Enter Cheque Number'
              value={chequeNumber}
              onChange={(e) => setChequeNumber(e.target.value)}
              fullWidth
              hasTopLabel
              required
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <DropdownSelect
              label='Bank name'
              placeholder='Select Bank Name'
              value={bankName}
              options={bankOptions}
              onChange={(e) => setBankName(e.target.value as string)}
              name='bankName'
              fullWidth
              hasTopLabel
              required
              isSearchable={true}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <TopLabelTextField
              label='Branch name'
              placeholder='Enter Branch Name'
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              fullWidth
              hasTopLabel
              required
            />
          </Box>
        </>
      )}

      <Box sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CustomDatePicker
            label='Deposit Upto Date'
            onChange={(newValue: Dayjs | null) => setDepositUptoDate(newValue)}
            value={depositUptoDate}
            required
          />
        </LocalizationProvider>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Remarks'
          placeholder='Enter Remarks'
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          rows={3}
          hasTopLabel
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <DropdownSelect
          label='Payment Status'
          placeholder='Select Payment Status'
          value={paymentStatus}
          options={paymentStatusOptions}
          onChange={(e) => setPaymentStatus(e.target.value as string)}
          name='paymentStatus'
          fullWidth
          hasTopLabel
          required
        />
      </Box>

      {!(
        paymentMode === ModeOfPayment.CHEQUE ||
        paymentMode === ModeOfPayment.CASH ||
        paymentMode === ModeOfPayment.DEMAND_DRAFT
      ) && (
        <Box sx={{ mb: 2 }}>
          <TopLabelTextField
            label='Transaction ID'
            placeholder='Enter Transaction ID'
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            fullWidth
            hasTopLabel
          />
        </Box>
      )}

      {isPaymentStatusChanged && (
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Status Change Date'
              onChange={(newValue: Dayjs | null) =>
                setUserSelectedStatusChangeDate(newValue)
              }
              value={userSelectedStatusChangeDate}
            />
          </LocalizationProvider>
        </Box>
      )}

      {isBouncedCheque && (
        <Box sx={{ mb: 2 }}>
          <DropdownSelect
            label='Cheque Bounce Reason'
            placeholder='Select Cheque Bounce Reason'
            value={chequeBounceReason}
            options={chequeBounceReasonOptions}
            onChange={(e) => setChequeBounceReason(e.target.value as string)}
            name='chequeBounceReason'
            fullWidth
            hasTopLabel
            required
          />
        </Box>
      )}

      {showPaymentFailureReason && (
        <Box sx={{ mb: 2 }}>
          <TopLabelTextField
            label='Payment Failure Reason'
            placeholder='Enter Payment Failure Reason'
            value={paymentFailureReason}
            onChange={(e) => setPaymentFailureReason(e.target.value)}
            fullWidth
            hasTopLabel
          />
        </Box>
      )}
    </DialogModal>
  );
};

export default UpdatePaymentStatusDialog;
